"use client"

import Link from "next/link"
import { IconChevronRight, IconCircleCheck, IconClock } from "@tabler/icons-react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import type { CalendarEvent } from "@/components/calendar/calendar-types"

interface NextPatientCardProps {
  /**
   * The next appointment of the day (first event whose `start` is >= now),
   * or null when nothing is left. Resolved by the parent so the today-window
   * calendar fetch is shared with the KPI band (one request, two consumers).
   */
  event: CalendarEvent | null
  /** Appointments still ahead today, `event` included. */
  remainingCount: number
  isLoading: boolean
  className?: string
}

/**
 * Doctor "My day" hero card — who is next, when, and one click to their chart.
 *
 * Purely presentational: the parent owns the fetch + the next-event selection,
 * so this never duplicates the today-window request that also feeds the
 * personal KPI band. The patient link is only rendered when the event payload
 * carries `lead.id`; otherwise it falls back to /calendar.
 */
export function NextPatientCard({
  event,
  remainingCount,
  isLoading,
  className,
}: NextPatientCardProps) {
  const t = useTranslations("dashboard")
  const formatter = useFormatter()

  if (isLoading) {
    return (
      <ApolloPanel eyebrow={t("nextPatient.title")} className={className}>
        <div className="space-y-3">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-5 w-24 rounded-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      </ApolloPanel>
    )
  }

  if (!event) {
    return (
      <ApolloPanel eyebrow={t("nextPatient.title")} className={className}>
        <div className="flex h-[180px] flex-col items-center justify-center text-center">
          <div className="mb-3 rounded-full bg-muted p-3">
            <IconCircleCheck className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {t("nextPatient.empty.title")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground/70">
            {t("nextPatient.empty.body")}
          </p>
        </div>
      </ApolloPanel>
    )
  }

  const start = new Date(event.start)
  const hasValidStart = !Number.isNaN(start.getTime())
  const patientName = event.lead?.full_name || event.title
  // Only chip the appointment title when it is not already the headline.
  const typeChip = event.lead?.full_name ? event.title : null
  const leadId = event.lead?.id

  return (
    <ApolloPanel eyebrow={t("nextPatient.title")} className={className}>
      <div className="flex flex-col gap-3">
        {hasValidStart && (
          <div className="flex items-center gap-2 text-text-dim">
            <IconClock className="h-4 w-4 shrink-0" />
            <span className="text-[15px] font-medium tabular-nums text-foreground">
              {formatter.dateTime(start, { hour: "2-digit", minute: "2-digit" })}
            </span>
            <span className="text-xs">{formatter.relativeTime(start)}</span>
          </div>
        )}

        <div className="font-serif text-[26px] leading-[1.1] tracking-[-0.01em] md:text-[30px]">
          {patientName}
        </div>

        {(typeChip || remainingCount > 1) && (
          <div className="flex flex-wrap items-center gap-2">
            {typeChip && (
              <Badge variant="secondary" className="text-[10px]">
                {typeChip}
              </Badge>
            )}
            {remainingCount > 1 && (
              <span className="text-[11px] text-text-dim">
                {t("nextPatient.moreAfter", { count: remainingCount - 1 })}
              </span>
            )}
          </div>
        )}

        <Button asChild variant="outline" size="sm" className="mt-1 justify-between">
          <Link href={leadId ? `/leads/${leadId}` : "/calendar"}>
            {leadId ? t("nextPatient.viewPatient") : t("nextPatient.viewCalendar")}
            <IconChevronRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </ApolloPanel>
  )
}
